import { useState } from 'react';
import { Modal, Button } from '@/components';
import { DATE_INPUT_MIN, DATE_INPUT_MAX } from '@/lib/dates';
import { easternTodayYmd } from '@/modules/manpower/lib/gridDates';
import { receivedProblem, revisionLabel } from './submittalRounds';

/**
 * Start the next review round: Send Revision asks for the one thing the screen cannot
 * know, the day the corrected package came back from the subcontractor, which is not
 * always today.
 *
 * ⚠️ Nothing is recorded here (#626). Confirming only moves on: to the Outlook draft
 * (`draft`) or, for an email that already went without it, straight to "when did you send
 * it?" (`record`). The round opens, and the submittal goes back into review, when the
 * person confirms the email was sent. A draft that never goes out must not start a
 * review clock or open a round, and neither can be put back.
 *
 * Styled with the `sub-*` classes SubmittalsTab declares. This only ever renders from
 * that tab's detail view.
 */

export default function SubmittalRevisionModal({
  revision,
  mode,
  onCancel,
  onConfirm,
}: {
  /**
   * The revision this opens, so the dialog says what it will do, or null when the
   * rounds have not loaded, could not be, or none is recorded yet (a submittal that
   * existed before the rounds did, until the backfill or its next write): then it says
   * "the next revision" rather than guess a number the server may count differently.
   */
  revision: number | null;
  /** `draft`: next is the Outlook draft. `record`: the email already went; next is the time it was sent. */
  mode: 'draft' | 'record';
  onCancel: () => void;
  onConfirm: (receivedAt: string) => void;
}) {
  // Today in EASTERN, not the browser's calendar: the sent time that follows defaults in
  // Eastern, and the API refuses a revision sent on a day before it was received. A browser
  // whose calendar is already tomorrow (UTC, or anywhere east of New York) near midnight would
  // otherwise offer a received day later than the sent day.
  const [received, setReceived] = useState(() => easternTodayYmd());
  const problem = receivedProblem(received);
  const which = revision === null ? 'the next revision' : revisionLabel(revision);

  return (
    <Modal
      isOpen
      onClose={onCancel}
      title={mode === 'draft' ? `Send ${which} for review` : `Mark ${which} as sent`}
      footer={
        <>
          <Button variant="secondary" onClick={onCancel}>Cancel</Button>
          <Button onClick={() => onConfirm(received)} disabled={problem !== null}>Continue</Button>
        </>
      }
    >
      <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--ew-s-3)' }}>
        <p className="sub-help">
          {mode === 'draft'
            ? 'Next you can open the Outlook draft. Nothing is recorded until you confirm the email has gone. '
            : `Use this when ${which} went out without the Outlook draft. Next you enter the time it was sent. `}
          Then {which} opens and the submittal goes back into review with a fresh review window.
          Earlier rounds, and the files on them, stay as they are.
        </p>
        <div className="sub-field">
          <label className="sub-label" htmlFor="sub-revision-received">Received from sub</label>
          <input
            id="sub-revision-received"
            className="sub-input"
            type="date"
            min={DATE_INPUT_MIN}
            max={DATE_INPUT_MAX}
            value={received}
            onChange={(e) => setReceived(e.target.value)}
          />
          <p className="sub-help" style={{ marginTop: 'var(--ew-s-2)' }}>
            The day the corrected package came back from the subcontractor.
          </p>
        </div>
        {problem && received !== '' ? (
          <p className="sub-help" style={{ color: 'var(--ew-danger)' }}>{problem}</p>
        ) : null}
      </div>
    </Modal>
  );
}
